/**
 * Startup hook that re-links orphaned transaction-contact references.
 *
 * After a wallet restore, Evolu row IDs change but domain identifiers
 * (Lightning Addresses, Nostr pubkeys, BOLT12 offers) remain stable.
 * This hook runs once on mount to re-link orphaned contactId references
 * on transactions using domain identifier matching.
 *
 * Tier: 2 (active users -- contacts).
 *
 * @module features/contacts/hooks/useTransactionContactLinker
 */

import { useEffect, useState, useRef } from 'react';
import { relinkTransactionsToContacts } from '@/features/contacts/utils/contact-relinker';
import type { TransactionForRelink } from '@/features/contacts/utils/contact-relinker';
import type { Contact } from '@/types/contact';

/** Data source interface for the transaction-contact linker. */
export interface TransactionContactLinkerDataSource {
  /** Transactions that may have orphaned contactId references. */
  readonly transactions: readonly TransactionForRelink[];
  /** Current contact list (post-restore with potentially new IDs). */
  readonly contacts: readonly Contact[];
  /** Update a transaction's contactId. */
  readonly updateTransactionContact: (txId: string, contactId: string) => void;
}

/** Return type for useTransactionContactLinker. */
export interface UseTransactionContactLinkerResult {
  /** Number of transactions that were re-linked. */
  readonly relinkedCount: number;
  /** Whether re-linking is currently in progress. */
  readonly isRelinking: boolean;
}

/**
 * Re-link orphaned transaction-contact references on startup.
 *
 * Runs once after mount when data is available. Uses domain identifiers
 * (lightningAddress, nostrPubkey, bolt12Offer) to find matching contacts
 * for transactions whose contactId no longer points to a valid contact.
 *
 * @param dataSource - Provides transactions, contacts, and update function
 * @returns Object with relinked count and loading state
 */
export function useTransactionContactLinker(
  dataSource: TransactionContactLinkerDataSource,
): UseTransactionContactLinkerResult {
  const [relinkedCount, setRelinkedCount] = useState(0);
  const [isRelinking, setIsRelinking] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Run once when data is loaded
    if (hasRunRef.current) return;
    if (dataSource.transactions.length === 0 && dataSource.contacts.length === 0) return;

    hasRunRef.current = true;
    setIsRelinking(true);

    const relinks = relinkTransactionsToContacts(dataSource.transactions, dataSource.contacts);

    for (const [txId, contactId] of relinks) {
      dataSource.updateTransactionContact(txId, contactId);
    }

    setRelinkedCount(relinks.size);
    setIsRelinking(false);
  }, [dataSource]);

  return { relinkedCount, isRelinking };
}
